import { useCallback, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import { Eye } from "lucide-react";

import { EmptyState } from "@/components/shared/EmptyState";
import { LoadingLogo } from "@/components/shared/LoadingLogo";
import { PageHeader } from "@/components/shared/PageHeader";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { TableCard } from "@/components/shared/TableCard";
import { TablePagination } from "@/components/shared/TablePagination";
import { TableActionsMenu, type TableAction } from "@/components/shared/TableActionsMenu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { usePaginationFromUrl } from "@/hooks/usePaginationFromUrl";
import { formatUyMinor } from "@/lib/formatUyMinor";
import { statusBadgeVariant, statusLabelKey } from "@/lib/professionalPayoutStatus";
import { listPlatformProfessionalPayoutRequests } from "@/services/professionalEarningsService";

export function PlatformProfessionalPayoutsPage(): JSX.Element {
  const { t } = useTranslation(undefined, { keyPrefix: "platformProfessionalPayouts" });
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const pagination = usePaginationFromUrl();

  const filterStatus = searchParams.get("status") ?? "";

  const setFilterStatus = useCallback(
    (value: string) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (value) next.set("status", value);
          else next.delete("status");
          next.set("page", "1");
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const listQuery = useQuery({
    queryKey: ["platform", "professional-payouts", filterStatus, pagination.page, pagination.limit],
    queryFn: () =>
      listPlatformProfessionalPayoutRequests({
        status: filterStatus || undefined,
        limit: pagination.limit,
        offset: (pagination.page - 1) * pagination.limit,
      }),
  });

  useEffect(() => {
    if (listQuery.data != null) {
      pagination.setTotal(listQuery.data.total);
    } else if (listQuery.isError) {
      pagination.setTotal(0);
    }
  }, [listQuery.data, listQuery.isError, pagination]);

  useEffect(() => {
    if (listQuery.isError) {
      toast.error(t("loadError"));
    }
  }, [listQuery.isError, t]);

  const rowActions = useCallback(
    (id: string): TableAction[] => [
      {
        label: t("view"),
        icon: Eye,
        onClick: () => navigate(`/professional-payouts/${id}`),
      },
    ],
    [navigate, t]
  );

  if (listQuery.isLoading) {
    return (
      <div className="p-4 md:p-8">
        <div className="flex min-h-[40vh] items-center justify-center">
          <LoadingLogo size="lg" />
        </div>
      </div>
    );
  }

  const items = listQuery.data?.items ?? [];

  return (
    <div className="p-4 md:p-8">
      <PageHeader title={t("pageTitle")} description={t("pageSubtitle")} />

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <Select value={filterStatus || "all"} onValueChange={(v) => setFilterStatus(v === "all" ? "" : v)}>
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder={t("filterStatus")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("filterAll")}</SelectItem>
            <SelectItem value="pending">{t("filterPending")}</SelectItem>
            <SelectItem value="processing">{t("filterProcessing")}</SelectItem>
            <SelectItem value="paid">{t("filterPaid")}</SelectItem>
            <SelectItem value="cancelled">{t("filterCancelled")}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <TableCard className="mt-4">
        {items.length === 0 ? (
          <div className="flex min-h-[40vh] items-center justify-center p-6">
            <EmptyState title={t("emptyTitle")} message={t("emptyMessage")} />
          </div>
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("columnProfessional")}</TableHead>
                  <TableHead>{t("columnAmount")}</TableHead>
                  <TableHead>{t("columnStatus")}</TableHead>
                  <TableHead>{t("columnCreated")}</TableHead>
                  <TableHead className="w-[80px]">{t("columnActions")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((row) => (
                  <TableRow
                    key={row.id}
                    className="cursor-pointer transition-colors"
                    onClick={() => navigate(`/professional-payouts/${row.id}`)}
                  >
                    <TableCell className="font-medium text-foreground">
                      {row.professionalName ?? row.professionalEmail ?? row.professionalUserId}
                    </TableCell>
                    <TableCell>{formatUyMinor(row.totalMinor, row.currency)}</TableCell>
                    <TableCell>
                      <StatusBadge variant={statusBadgeVariant(row.status)}>
                        {t(statusLabelKey(row.status))}
                      </StatusBadge>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {new Date(row.createdAt).toLocaleString()}
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <TableActionsMenu actions={rowActions(row.id)} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              pagination={pagination}
              rowsPerPageLabel={t("rowsPerPage")}
              pageOfLabel={t("pageOf", {
                from:
                  listQuery.data?.total === 0
                    ? 0
                    : (pagination.page - 1) * pagination.limit + 1,
                to: Math.min(
                  pagination.page * pagination.limit,
                  listQuery.data?.total ?? 0
                ),
                total: listQuery.data?.total ?? 0,
              })}
              onRefresh={() => void listQuery.refetch()}
              isRefreshing={listQuery.isFetching}
            />
          </>
        )}
      </TableCard>
    </div>
  );
}
